import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import {
  getPropertyCostOfSales,
  addPropertyExpense,
  updatePropertyExpense,
  deletePropertyExpense,
  type CostOfSales,
  type SalesExpense,
  type CreateSalesExpenseRequest,
} from '../services/commissionService';
import { NumericFormat } from 'react-number-format';

interface CostOfSalesPanelProps {
  agencySlug: string;
  propertyId: string;
  canEdit?: boolean;
}

const expenseCategories = [
  { value: 'campaign_fee', label: 'Campaign Fee' },
  { value: 'doc_sign_fee', label: 'Document Signing Fee' },
  { value: 'ad_hoc', label: 'Ad-hoc Expense' },
  { value: 'other', label: 'Other' },
];

const CostOfSalesPanel: React.FC<CostOfSalesPanelProps> = ({
  agencySlug,
  propertyId,
  canEdit = false,
}) => {
  const [costOfSales, setCostOfSales] = useState<CostOfSales | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<SalesExpense | null>(null);
  const [formData, setFormData] = useState<CreateSalesExpenseRequest>({
    category: 'campaign_fee',
    description: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCostOfSales();
  }, [agencySlug, propertyId]);

  const loadCostOfSales = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getPropertyCostOfSales(agencySlug, propertyId);
      setCostOfSales(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load cost of sales');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (expense?: SalesExpense) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        category: expense.category,
        description: expense.description,
        amount: expense.amount,
        date: expense.date.split('T')[0],
        notes: expense.notes || '',
      });
    } else {
      setEditingExpense(null);
      setFormData({
        category: 'campaign_fee',
        description: '',
        amount: 0,
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
  };

  const handleSave = async () => {
    if (!formData.description || formData.amount <= 0) {
      setError('Description and amount are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingExpense) {
        await updatePropertyExpense(
          agencySlug,
          propertyId,
          editingExpense.id,
          formData
        );
      } else {
        await addPropertyExpense(agencySlug, propertyId, formData);
      }

      await loadCostOfSales();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expenseId: string) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      setError(null);
      await deletePropertyExpense(agencySlug, propertyId, expenseId);
      await loadCostOfSales();
    } catch (err: any) {
      setError(err.message || 'Failed to delete expense');
    }
  };

  const getCategoryLabel = (category: string) => {
    return expenseCategories.find(c => c.value === category)?.label || category;
  };

  const getCategoryColor = (category: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (category) {
      case 'campaign_fee':
        return 'primary';
      case 'doc_sign_fee':
        return 'secondary';
      case 'ad_hoc':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3">
            Cost of Sales
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Expense
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Total Cost of Sales
          </Typography>
          <Typography variant="h5" color="error.main">
            ${(costOfSales?.totalCost || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Excludes marketing fees charged to vendor
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {costOfSales && costOfSales.expenses.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  {canEdit && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {costOfSales.expenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      {new Date(expense.date).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(expense.category)}
                        size="small"
                        color={getCategoryColor(expense.category)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {expense.description}
                      {expense.notes && (
                        <Tooltip title={expense.notes}>
                          <ReceiptIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        ${expense.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(expense)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(expense.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">
            No expenses recorded yet. Add expenses as they occur (campaign fees, document signing
            fees, etc.). Marketing fees charged to vendors are not included in cost of sales.
          </Alert>
        )}

        {/* Add/Edit Expense Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingExpense ? 'Edit Expense' : 'Add Expense'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                select
                label="Category"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value as any })
                }
                fullWidth
                margin="normal"
                required
              >
                {expenseCategories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                fullWidth
                margin="normal"
                required
                multiline
                rows={2}
              />

              <NumericFormat
                customInput={TextField}
                label="Amount ($)"
                value={formData.amount}
                onValueChange={(values) => {
                  setFormData({ ...formData, amount: values.floatValue || 0 });
                }}
                thousandSeparator={true}
                prefix="$"
                decimalScale={2}
                allowNegative={false}
                fullWidth
                margin="normal"
                required
              />

              <TextField
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                fullWidth
                margin="normal"
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                label="Notes (Optional)"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || !formData.description || formData.amount <= 0}
            >
              {saving ? <CircularProgress size={20} /> : editingExpense ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CostOfSalesPanel;
